import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateItemsOptions,
  BulkCreateItemsResponse,
  BulkDeleteItemsResponse,
  BulkUpdateItemsOptions,
  BulkUpdateItemsResponse,
  GetDeletedItemResponse,
  Item,
  ItemQuery,
  ItemsQueryBuilder,
  ListDeletedItemsOptions,
  ListDeletedItemsResponse,
  MaskedItem,
  QueryItemsResponse,
  RestoreItemFromTrashBinResponse,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  UpdateItem,
  bulkCreateItems as universalBulkCreateItems,
  bulkDeleteItems as universalBulkDeleteItems,
  bulkUpdateItems as universalBulkUpdateItems,
  createItem as universalCreateItem,
  deleteItem as universalDeleteItem,
  getDeletedItem as universalGetDeletedItem,
  getItem as universalGetItem,
  listDeletedItems as universalListDeletedItems,
  queryItems as universalQueryItems,
  removeItemFromTrashBin as universalRemoveItemFromTrashBin,
  restoreItemFromTrashBin as universalRestoreItemFromTrashBin,
  typedQueryItems as universalTypedQueryItems,
  updateExtendedFields as universalUpdateExtendedFields,
  updateItem as universalUpdateItem,
} from './fastgallery-item-v1-item-items.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/fast-gallery' };

export function createItem(httpClient: HttpClient): CreateItemSignature {
  return (item: Item) =>
    universalCreateItem(
      item,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateItemSignature {
  /**
   * create item (todo: tech writer to add more details)
   * @param - Item to be created.
   * @returns The created Item.
   */
  (item: Item): Promise<
    NonNullablePaths<
      Item,
      | `media.imageOptions.focalPoint.x`
      | `media.imageOptions.focalPoint.y`
      | `media.type`,
      5
    >
  >;
}

export function getItem(httpClient: HttpClient): GetItemSignature {
  return (itemId: string) =>
    universalGetItem(
      itemId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetItemSignature {
  /**
   * Retrieves a Item.
   * @param - ID of the Item to retrieve.
   * @returns The requested Item.
   */
  (itemId: string): Promise<
    NonNullablePaths<
      Item,
      | `media.imageOptions.focalPoint.x`
      | `media.imageOptions.focalPoint.y`
      | `media.type`,
      5
    >
  >;
}

export function updateItem(httpClient: HttpClient): UpdateItemSignature {
  return (_id: string, item: NonNullablePaths<UpdateItem, `revision`, 2>) =>
    universalUpdateItem(
      _id,
      item,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateItemSignature {
  /**
   * Updates a Item.
   *
   * Each time the Item is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the Item.
   * This ensures you're working with the latest Item
   * and prevents unintended overwrites.
   * @param - Item ID.
   * @returns Updated Item.
   */
  (_id: string, item: NonNullablePaths<UpdateItem, `revision`, 2>): Promise<
    NonNullablePaths<
      Item,
      | `media.imageOptions.focalPoint.x`
      | `media.imageOptions.focalPoint.y`
      | `media.type`,
      5
    >
  >;
}

export function deleteItem(httpClient: HttpClient): DeleteItemSignature {
  return (itemId: string) =>
    universalDeleteItem(
      itemId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteItemSignature {
  /**
   * delete item (todo: tech writer to add more details)
   * @param - Id of the Item to delete.
   */
  (itemId: string): Promise<void>;
}

export function queryItems(httpClient: HttpClient): QueryItemsSignature {
  return () =>
    universalQueryItems(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryItemsSignature {
  /**
   * Creates a query to retrieve a list of gallery items.
   *
   * The `queryItems()` method builds a query to retrieve a list of gallery items and returns an `ItemsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `ItemsQueryBuilder` methods onto the query. `ItemsQueryBuilder` methods enable you to filter, sort, and control the results that `queryItems()` returns.
   */
  (): ItemsQueryBuilder;
}

export function typedQueryItems(
  httpClient: HttpClient
): TypedQueryItemsSignature {
  return (query: ItemQuery) =>
    universalTypedQueryItems(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryItemsSignature {
  /** */
  (query: ItemQuery): Promise<
    NonNullablePaths<
      QueryItemsResponse,
      `items` | `items.${number}.media.type`,
      5
    >
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields of a Item without incrementing revision
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      | `item.media.imageOptions.focalPoint.x`
      | `item.media.imageOptions.focalPoint.y`
      | `item.media.type`,
      6
    >
  >;
}

export function bulkCreateItems(
  httpClient: HttpClient
): BulkCreateItemsSignature {
  return (items: Item[], options?: BulkCreateItemsOptions) =>
    universalBulkCreateItems(
      items,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateItemsSignature {
  /**
   * Create multiple Items in a single request. Works synchronously.
   * @param - List of Items to be created
   */
  (items: Item[], options?: BulkCreateItemsOptions): Promise<
    NonNullablePaths<
      BulkCreateItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.media.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateItems(
  httpClient: HttpClient
): BulkUpdateItemsSignature {
  return (
    items: NonNullablePaths<MaskedItem, `item.revision`, 3>[],
    options?: BulkUpdateItemsOptions
  ) =>
    universalBulkUpdateItems(
      items,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateItemsSignature {
  /**
   * Update multiple Items in a single request. Works synchronously.
   * @param - List of Items to be updated.
   */
  (
    items: NonNullablePaths<MaskedItem, `item.revision`, 3>[],
    options?: BulkUpdateItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.media.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteItems(
  httpClient: HttpClient
): BulkDeleteItemsSignature {
  return (itemIds: string[]) =>
    universalBulkDeleteItems(
      itemIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteItemsSignature {
  /**
   * Delete multiple Items in a single request. Works synchronously.
   * @param - Item ids to be deleted
   */
  (itemIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function removeItemFromTrashBin(
  httpClient: HttpClient
): RemoveItemFromTrashBinSignature {
  return (itemId: string) =>
    universalRemoveItemFromTrashBin(
      itemId,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveItemFromTrashBinSignature {
  /**
   * Removes deleted Item from TrashBin
   * @param - ID of the Item to remove from TrashBin.
   */
  (itemId: string): Promise<void>;
}

export function restoreItemFromTrashBin(
  httpClient: HttpClient
): RestoreItemFromTrashBinSignature {
  return (itemId: string) =>
    universalRestoreItemFromTrashBin(
      itemId,
      // @ts-ignore
      { httpClient }
    );
}

interface RestoreItemFromTrashBinSignature {
  /**
   * Restores an Item from TrashBin
   * @param - ID of the Item to restore from TrashBin.
   */
  (itemId: string): Promise<
    NonNullablePaths<
      RestoreItemFromTrashBinResponse,
      | `item.media.imageOptions.focalPoint.x`
      | `item.media.imageOptions.focalPoint.y`
      | `item.media.type`,
      6
    >
  >;
}

export function getDeletedItem(
  httpClient: HttpClient
): GetDeletedItemSignature {
  return (itemId: string) =>
    universalGetDeletedItem(
      itemId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDeletedItemSignature {
  /**
   * Get deleted Item from TrashBin
   * @param - ID of the Item to retrieve from TrashBin.
   */
  (itemId: string): Promise<
    NonNullablePaths<
      GetDeletedItemResponse,
      | `item.media.imageOptions.focalPoint.x`
      | `item.media.imageOptions.focalPoint.y`
      | `item.media.type`,
      6
    >
  >;
}

export function listDeletedItems(
  httpClient: HttpClient
): ListDeletedItemsSignature {
  return (options?: ListDeletedItemsOptions) =>
    universalListDeletedItems(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListDeletedItemsSignature {
  /**
   * List deleted Items which are in TrashBin
   */
  (options?: ListDeletedItemsOptions): Promise<
    NonNullablePaths<
      ListDeletedItemsResponse,
      `items` | `items.${number}.media.type`,
      5
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AddressLink,
  AnchorLink,
  ApplicationError,
  BulkActionMetadata,
  BulkCreateItemsOptions,
  BulkCreateItemsRequest,
  BulkCreateItemsResponse,
  BulkDeleteItemsRequest,
  BulkDeleteItemsResponse,
  BulkDeleteItemsResponseBulkItemResult,
  BulkItemResult,
  BulkUpdateItemsOptions,
  BulkUpdateItemsRequest,
  BulkUpdateItemsResponse,
  BulkUpdateItemsResponseBulkItemResult,
  CreateItemRequest,
  CreateItemResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteItemRequest,
  DeleteItemResponse,
  DocumentLink,
  DomainEvent,
  DomainEventBodyOneOf,
  DynamicPageLink,
  EmailLink,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  ExternalLink,
  FocalPoint,
  GetDeletedItemRequest,
  GetDeletedItemResponse,
  GetItemRequest,
  GetItemResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  Item,
  ItemMetadata,
  ItemQuerySpec,
  ItemsQueryBuilder,
  ItemsQueryResult,
  Link,
  LinkRel,
  LinkType,
  ListDeletedItemsOptions,
  ListDeletedItemsRequest,
  ListDeletedItemsResponse,
  MaskedItem,
  MediaItem,
  MediaItemMediaOneOf,
  MessageEnvelope,
  PageLink,
  PhoneLink,
  Point,
  QueryItemsRequest,
  QueryItemsResponse,
  RemoveItemFromTrashBinRequest,
  RemoveItemFromTrashBinResponse,
  RestoreInfo,
  RestoreItemFromTrashBinRequest,
  RestoreItemFromTrashBinResponse,
  SortOrder,
  Sorting,
  TpaPageLink,
  Type,
  UnsharpMasking,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UpdateItem,
  UpdateItemRequest,
  UpdateItemResponse,
  WebhookIdentityType,
  WhatsAppLink,
  WixLink,
  WixLinkLinkOneOf,
  utils,
} from './fastgallery-item-v1-item-items.universal.js';
